<template>
    <div v-if="resourceId && selectedDate" class="space-y-2">
        <!-- Loading skeleton -->
        <div v-if="loading" class="rounded-lg bg-surface-50 px-6 py-4">
            <div class="flex justify-between mb-2">
                <Skeleton width="3rem" height="1rem" />
                <Skeleton width="3rem" height="1rem" />
                <Skeleton width="3rem" height="1rem" />
                <Skeleton width="3rem" height="1rem" />
                <Skeleton width="3rem" height="1rem" />
            </div>
            <Skeleton width="100%" height="2.5rem" />
            <div class="flex gap-3 mt-2">
                <Skeleton width="5rem" height="1rem" />
                <Skeleton width="5rem" height="1rem" />
                <Skeleton width="5rem" height="1rem" />
            </div>
        </div>

        <!-- No availability warning -->
        <div v-else-if="!hasAvailability" class="p-3 rounded-lg bg-orange-50 border border-orange-200">
            <div class="flex items-center gap-2 text-orange-700">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>{{ t('calendar.new.resourcesSlots.timeline.noAvailability') }}</span>
            </div>
        </div>

        <!-- Timeline visualization -->
        <div v-else class="rounded-lg bg-surface-50 px-6 py-4">
            <!-- Hour markers -->
            <div class="relative h-4 mb-2 px-2">
                <div v-for="(hour, index) in timelineHours" :key="`${hour}-${index}`" v-show="showHourLabel(index)"
                    class="absolute text-sm text-gray-500"
                    :style="{ left: `${timeToPercent(hour)}%`, transform: 'translateX(-50%)' }">
                    {{ hour }}
                </div>
            </div>

            <!-- Timeline bar (drag to select) -->
            <div ref="timelineBarRef"
                class="relative h-8 sm:h-10 bg-green-100 rounded overflow-hidden cursor-crosshair select-none"
                @mousedown.prevent="onTimelineMouseDown" @touchstart.prevent="onTimelineTouchStart"
                @touchmove.prevent="onTimelineTouchMove" @touchend.prevent="onTimelineTouchEnd"
                @touchcancel="onTimelineTouchCancel" @mousemove="onTimelineHoverMove"
                @mouseleave="onTimelineHoverLeave">
                <!-- Maintenance blocks -->
                <div v-for="(block, index) in maintenanceBlocks" :key="`maint-${index}`"
                    class="absolute top-0 h-full bg-gray-400 flex items-center justify-center pointer-events-none"
                    :class="block.reducedCapacity != null ? 'opacity-50 timeline-block--partial' : 'opacity-70'"
                    :style="{ left: `${block.left}%`, width: `${block.width}%` }">
                    <span v-if="block.width > 15" class="text-sm text-white font-medium truncate px-1">
                        <i class="fa-solid fa-wrench mr-1"></i>
                        {{ block.title }}<template v-if="block.reducedCapacity != null"> · {{ t('calendar.new.resourcesSlots.timeline.maxCapacity', { capacity: block.reducedCapacity }) }}</template>
                    </span>
                </div>

                <!-- Reservation blocks -->
                <div v-for="block in reservationBlocks" :key="`res-${block.id}`"
                    class="absolute top-0 h-full bg-blue-500 opacity-80 flex items-center justify-center pointer-events-none"
                    :style="{ left: `${block.left}%`, width: `${block.width}%` }">
                    <span v-if="block.width > 15" class="text-sm text-white font-medium truncate px-1">
                        {{ block.title }}
                    </span>
                </div>

                <!-- Selected time range indicator -->
                <div v-if="startTime && endTime"
                    class="absolute top-0 h-full border-2 border-purple-600 bg-purple-200 opacity-50 rounded pointer-events-none"
                    :style="{
                        left: `${timeToPercent(startTime)}%`,
                        width: `${timeToPercent(endTime) - timeToPercent(startTime)}%`
                    }">
                </div>
            </div>

            <!-- Legend -->
            <div
                class="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-2 mt-2 text-sm w-full">
                <div class="flex flex-wrap gap-3 w-full sm:w-auto">
                    <div class="flex items-center gap-1 min-w-[120px]">
                        <div class="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                        <span class="truncate">{{ t('calendar.new.resourcesSlots.timeline.available') }}</span>
                    </div>
                    <div class="flex items-center gap-1 min-w-[120px]">
                        <div class="w-3 h-3 rounded bg-blue-500"></div>
                        <span class="truncate">{{ t('calendar.new.resourcesSlots.timeline.reservations') }}</span>
                    </div>
                    <div class="flex items-center gap-1 min-w-[120px]">
                        <div class="w-3 h-3 rounded bg-gray-400"></div>
                        <span class="truncate">{{ t('calendar.new.resourcesSlots.timeline.maintenance') }}</span>
                    </div>
                    <!-- Gated so the common all-fully-blocking case does not permanently widen the legend (D-09). -->
                    <div v-if="hasPartialMaintenance" class="flex items-center gap-1 min-w-[120px]">
                        <div class="w-3 h-3 rounded bg-gray-400 opacity-50 timeline-block--partial"></div>
                        <span class="truncate">{{ t('calendar.new.resourcesSlots.timeline.partialMaintenance') }}</span>
                    </div>
                    <div v-if="startTime && endTime" class="flex items-center gap-1 min-w-[120px]">
                        <div class="w-3 h-3 rounded bg-purple-200 border-2 border-purple-600"></div>
                        <span class="truncate">{{ t('calendar.new.resourcesSlots.timeline.selected') }}</span>
                    </div>
                </div>
                <div class="flex items-center gap-1 text-gray-500 w-full sm:w-auto">
                    <i class="fa-solid fa-info-circle"></i>
                    <span class="truncate">{{ t('calendar.new.resourcesSlots.timeline.dragHint') }}</span>
                </div>
            </div>
        </div>

        <!-- Per-block tooltip. Teleports to body itself, so its position here is immaterial. -->
        <TimelineBlockTooltip :block="tooltipBlock" :anchor-rect="tooltipAnchor" />
    </div>
</template>

<script setup lang="ts">
import TimelineBlockTooltip from '@/components/reservation/TimelineBlockTooltip.vue';
import { LONG_PRESS_MS, SHOW_DELAY_MS, TOUCH_MOVE_CANCEL_PX } from '@/composables/useCalendarEventPreview';
// The single declaration. TimelineBlockTooltip.vue already imports it from here, so a local copy in this file
// meant parent and child were typed by two structurally-compatible-by-luck interfaces with no compile error at
// the boundary between them.
import type { TimelineBlock } from '@/composables/useSlotDialog';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// How long a long-pressed tooltip survives after the finger lifts. Touch has no mouseleave, so without this the
// tooltip would sit over an open SlotDialog indefinitely. Timeline-local on purpose: it is neither LONG_PRESS_MS
// nor SHOW_DELAY_MS and does not belong in useCalendarEventPreview.ts.
const TOUCH_TOOLTIP_DISMISS_MS = 4000;

interface Props {
    resourceId: string | null;
    selectedDate: Date | null;
    startTime: string | null;
    endTime: string | null;
    loading: boolean;
    hasAvailability: boolean;
    timelineBounds: { start: string; end: string } | null;
    reservationBlocks: TimelineBlock[];
    maintenanceBlocks: TimelineBlock[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:startTime': [time: string];
    'update:endTime': [time: string];
}>();

const timelineBarRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const dragStartPercent = ref(0);

// Drives the gated partial-maintenance legend entry (D-09). A cap is present on a block iff the maintenance is
// partially blocking, so `!= null` is the whole test — see TimelineBlock.reducedCapacity.
const hasPartialMaintenance = computed(() => props.maintenanceBlocks.some(block => block.reducedCapacity != null));

// Timeline hours computed
const timelineHours = computed(() => {
    if (!props.timelineBounds) return [];
    const hours: string[] = [];
    const [startH, startM] = props.timelineBounds.start.split(':').map(Number);
    const [endH, endM] = props.timelineBounds.end.split(':').map(Number);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    for (let minutes = startMinutes; minutes <= endMinutes; minutes += 60) {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        hours.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }

    if (hours[hours.length - 1] !== props.timelineBounds.end) {
        hours.push(props.timelineBounds.end);
    }

    return hours;
});

// Reactive screen size for hour label logic
import { onMounted, onUnmounted, ref as vueRef } from 'vue';
const isMobile = vueRef(false);
function updateIsMobile() {
    isMobile.value = window.matchMedia('(max-width: 640px)').matches;
}
onMounted(() => {
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    // The tooltip is position: fixed and pinned to a DOMRect captured at show time, so any scroll or resize
    // invalidates its placement -- the same reasoning as handleViewportChange in useCalendarEventPreview.ts.
    // Registered here rather than inside the tooltip so it stays presentational, with no emits of its own.
    // Capture phase because the SlotDialog content region scrolls and scroll does not bubble to window.
    window.addEventListener('scroll', hideTooltip, { capture: true, passive: true });
    window.addEventListener('resize', hideTooltip);
});
onUnmounted(() => {
    window.removeEventListener('resize', updateIsMobile);
    window.removeEventListener('scroll', hideTooltip, true);
    window.removeEventListener('resize', hideTooltip);
    // onTimelineMouseDown binds these to document and only onTimelineMouseUp unbinds them. This component lives
    // inside a Dialog that unmounts on close, so a dialog dismissed mid-drag (Escape, mask click, a v-if flip on
    // currentResourceId) would never reach mouseup and would leak the pair for the rest of the session -- still
    // with isDragging true, emitting into a destroyed instance on every mouse move.
    document.removeEventListener('mousemove', onTimelineMouseMove);
    document.removeEventListener('mouseup', onTimelineMouseUp);
    // All three tooltip timers, so a dialog closed mid-gesture never fires one against a dead ref.
    clearHoverShowTimer();
    clearLongPressTimer();
    clearTouchDismissTimer();
});

function showHourLabel(index: number): boolean {
    if (isMobile.value) {
        // On mobile, show only every 2h
        return index % 2 === 0 || index === timelineHours.value.length - 1;
    }
    // On desktop, show all
    return true;
}

// Convert HH:mm time string to percentage position on timeline
function timeToPercent(time: string): number {
    if (!props.timelineBounds) return 0;

    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m;

    const [startH, startM] = props.timelineBounds.start.split(':').map(Number);
    const [endH, endM] = props.timelineBounds.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const rangeMinutes = endMinutes - startMinutes;

    if (rangeMinutes <= 0) return 0;

    return Math.max(0, Math.min(100, ((totalMinutes - startMinutes) / rangeMinutes) * 100));
}

// Convert percentage position to HH:mm time string (snapped to 15-minute intervals)
function percentToTime(percent: number): string {
    if (!props.timelineBounds) return '00:00';

    const [startH, startM] = props.timelineBounds.start.split(':').map(Number);
    const [endH, endM] = props.timelineBounds.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const rangeMinutes = endMinutes - startMinutes;

    const totalMinutes = startMinutes + (percent / 100) * rangeMinutes;
    // Snap to 15-minute intervals
    const snappedMinutes = Math.round(totalMinutes / 15) * 15;
    const clampedMinutes = Math.max(startMinutes, Math.min(endMinutes, snappedMinutes));

    const hours = Math.floor(clampedMinutes / 60);
    const minutes = clampedMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// Get mouse/touch position as percentage of timeline bar
function getTimelinePercent(event: MouseEvent | TouchEvent): number {
    if (!timelineBarRef.value) return 0;
    const rect = timelineBarRef.value.getBoundingClientRect();
    let x = 0;
    if (event instanceof MouseEvent) {
        x = event.clientX - rect.left;
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
        x = event.touches[0].clientX - rect.left;
    }
    return Math.max(0, Math.min(100, (x / rect.width) * 100));
}

// === Block tooltip (D-13 … D-16) ===
// Detection is geometry, not pointer events: every block keeps `pointer-events-none` so drag-to-select passes
// straight through it, which also means the browser never hovers a block. The pointer position is converted to a
// percentage and tested against each block's own left / left+width instead.

const tooltipBlock = ref<TimelineBlock | null>(null);
const tooltipAnchor = ref<DOMRect | null>(null);

let hoverShowTimer: ReturnType<typeof setTimeout> | null = null;
let hoverPendingBlock: TimelineBlock | null = null;
let longPressTimer: ReturnType<typeof setTimeout> | null = null;
let longPressOrigin: { x: number; y: number } | null = null;
let touchDismissTimer: ReturnType<typeof setTimeout> | null = null;

// Touch gesture arbitration (D-16). A touch on the bar is ambiguous until it resolves:
//   'pending'  — just landed; could still become any of the three below
//   'dragging' — travelled past TOUCH_MOVE_CANCEL_PX, so it is a drag-select and owns the range emits
//   'reading'  — reached LONG_PRESS_MS over a block and opened its tooltip, so it must write nothing
//   back to 'idle' on lift / cancel
// A short lift while still 'pending', or a hold over empty timeline where there is no tooltip to read,
// stays a plain point select. The selection write is therefore deferred out of touchstart and replayed
// from `pendingTouchSelect` only once the gesture is known — otherwise long-pressing a block to see who
// holds it would silently collapse the user's chosen range to a zero-length slot.
let touchPhase: 'idle' | 'pending' | 'dragging' | 'reading' = 'idle';
let pendingTouchSelect: string | null = null;

function commitPendingTouchSelect() {
    if (pendingTouchSelect === null) {
        return;
    }
    emit('update:startTime', pendingTouchSelect);
    emit('update:endTime', pendingTouchSelect);
    pendingTouchSelect = null;
}

function clearHoverShowTimer() {
    if (hoverShowTimer !== null) {
        clearTimeout(hoverShowTimer);
        hoverShowTimer = null;
    }
    hoverPendingBlock = null;
}

function clearLongPressTimer() {
    if (longPressTimer !== null) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
    }
    longPressOrigin = null;
}

function clearTouchDismissTimer() {
    if (touchDismissTimer !== null) {
        clearTimeout(touchDismissTimer);
        touchDismissTimer = null;
    }
}

// The single show funnel, shared by the hover timer and the long-press timer. It deliberately carries no
// isDragging test: that gate belongs at the mouse-path callers only, because isDragging is true for the whole
// duration of any touch contact and gating here would make the D-16 long-press unreachable.
function showTooltipForBlock(block: TimelineBlock, anchorRect: DOMRect | null) {
    if (anchorRect === null) {
        return;
    }
    tooltipBlock.value = block;
    tooltipAnchor.value = anchorRect;
}

// Clears every timer capable of re-showing the tooltip, the long-press one included. Leaving that one armed made
// a function named hideTooltip able to show a tooltip 500ms later — onTimelineMouseDown calls this without
// re-arming, so on a device dispatching both touch and mouse a touchstart-then-mousedown sequence would fire it
// mid-drag-select, exactly the state onTimelineHoverMove's isDragging guard exists to prevent.
function hideTooltip() {
    clearHoverShowTimer();
    clearLongPressTimer();
    clearTouchDismissTimer();
    tooltipBlock.value = null;
    tooltipAnchor.value = null;
}

// Last match wins: reservation blocks render after maintenance blocks in the DOM, so scanning maintenance first
// and keeping the final hit matches whichever block the user actually sees on top.
function findBlockAtPercent(percent: number): TimelineBlock | null {
    let match: TimelineBlock | null = null;
    for (const block of props.maintenanceBlocks) {
        if (percent >= block.left && percent <= block.left + block.width) {
            match = block;
        }
    }
    for (const block of props.reservationBlocks) {
        if (percent >= block.left && percent <= block.left + block.width) {
            match = block;
        }
    }
    return match;
}

// Anchor on the block, not the pointer, so the tooltip stays centred even when the pointer is at a block edge.
function getBlockAnchorRect(block: TimelineBlock): DOMRect | null {
    if (!timelineBarRef.value) return null;
    const barRect = timelineBarRef.value.getBoundingClientRect();
    const blockLeft = barRect.left + (block.left / 100) * barRect.width;
    const blockWidth = (block.width / 100) * barRect.width;
    return new DOMRect(blockLeft, barRect.top, blockWidth, barRect.height);
}

function onTimelineHoverMove(event: MouseEvent) {
    // Mouse path only. Here isDragging genuinely means "a drag-select is in progress" (set in onTimelineMouseDown,
    // cleared in onTimelineMouseUp), so the tooltip never occludes the range being dragged.
    if (isDragging.value) {
        clearHoverShowTimer();
        return;
    }
    if (!props.timelineBounds) return;

    const block = findBlockAtPercent(getTimelinePercent(event));
    if (block === null) {
        hideTooltip();
        return;
    }

    // Already open: swap block and anchor immediately, with no second show delay.
    if (tooltipBlock.value !== null) {
        clearHoverShowTimer();
        showTooltipForBlock(block, getBlockAnchorRect(block));
        return;
    }

    // A timer already armed for this same block must be left to run, or a moving pointer would restart it forever.
    if (hoverShowTimer !== null && hoverPendingBlock === block) {
        return;
    }

    clearHoverShowTimer();
    hoverPendingBlock = block;
    hoverShowTimer = setTimeout(() => {
        hoverShowTimer = null;
        hoverPendingBlock = null;
        showTooltipForBlock(block, getBlockAnchorRect(block));
    }, SHOW_DELAY_MS);
}

function onTimelineHoverLeave() {
    hideTooltip();
}

// Touch long-press (D-16). Never gated on isDragging: onTimelineTouchStart flips that flag true on the very first
// touchstart and clears it only at touchend, so it is true even for a completely stationary finger. What suppresses
// the tooltip during a real touch drag is the TOUCH_MOVE_CANCEL_PX travel cancellation below.
function startTimelineLongPress(event: TouchEvent) {
    clearLongPressTimer();
    if (event.touches.length === 0) return;

    const touch = event.touches[0];
    longPressOrigin = { x: touch.clientX, y: touch.clientY };
    const pressPercent = getTimelinePercent(event);

    longPressTimer = setTimeout(() => {
        longPressTimer = null;
        longPressOrigin = null;
        const block = findBlockAtPercent(pressPercent);
        if (block === null) {
            // Nothing to read over empty timeline, so this stays a select gesture and keeps its deferred write.
            return;
        }
        // The press became a read gesture: drop the deferred selection instead of committing it on lift.
        touchPhase = 'reading';
        pendingTouchSelect = null;
        showTooltipForBlock(block, getBlockAnchorRect(block));
        armTouchDismissTimer();
    }, LONG_PRESS_MS);
}

function moveTimelineLongPress(event: TouchEvent) {
    if (longPressTimer === null || longPressOrigin === null || event.touches.length === 0) return;

    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - longPressOrigin.x);
    const dy = Math.abs(touch.clientY - longPressOrigin.y);

    if (dx > TOUCH_MOVE_CANCEL_PX || dy > TOUCH_MOVE_CANCEL_PX) {
        // Travel resolves the ambiguity the other way: this is a drag-select, so the range emits may start.
        if (touchPhase === 'pending') {
            touchPhase = 'dragging';
        }
        clearLongPressTimer();
    }
}

// Self-expiry is the primary touch dismissal; hiding on the next touchstart is the second layer. No document-level
// outside-tap listener and no close button: the tooltip is pointer-events: none and read-only, so it can neither
// trap input nor hide a control the user is reaching for.
function armTouchDismissTimer() {
    clearTouchDismissTimer();
    touchDismissTimer = setTimeout(() => {
        touchDismissTimer = null;
        hideTooltip();
    }, TOUCH_TOOLTIP_DISMISS_MS);
}

function onTimelineTouchCancel() {
    clearLongPressTimer();
    // An aborted gesture writes nothing and leaves the existing selection intact. isDragging is cleared here
    // too: touchcancel previously left it stuck true, which permanently disabled the mouse hover tooltip.
    pendingTouchSelect = null;
    touchPhase = 'idle';
    isDragging.value = false;
}

// Timeline drag handlers
function onTimelineMouseDown(event: MouseEvent) {
    hideTooltip();
    if (!props.timelineBounds) return;
    isDragging.value = true;
    dragStartPercent.value = getTimelinePercent(event);
    const time = percentToTime(dragStartPercent.value);
    emit('update:startTime', time);
    emit('update:endTime', time);
    // Add global listeners for drag
    document.addEventListener('mousemove', onTimelineMouseMove);
    document.addEventListener('mouseup', onTimelineMouseUp);
}

function onTimelineMouseMove(event: MouseEvent) {
    if (!isDragging.value || !props.timelineBounds) return;
    const currentPercent = getTimelinePercent(event);
    const startTime = percentToTime(Math.min(dragStartPercent.value, currentPercent));
    const endTime = percentToTime(Math.max(dragStartPercent.value, currentPercent));
    emit('update:startTime', startTime);
    emit('update:endTime', endTime);
}

function onTimelineMouseUp() {
    isDragging.value = false;
    document.removeEventListener('mousemove', onTimelineMouseMove);
    document.removeEventListener('mouseup', onTimelineMouseUp);
}

// Touch drag handlers for mobile
function onTimelineTouchStart(event: TouchEvent) {
    hideTooltip();
    if (!props.timelineBounds) return;
    isDragging.value = true;
    dragStartPercent.value = getTimelinePercent(event);
    // Deferred, not emitted: a long-press is a read gesture and must leave startTime/endTime alone (D-16).
    // Replayed by commitPendingTouchSelect() once the gesture resolves to anything other than 'reading'.
    touchPhase = 'pending';
    pendingTouchSelect = percentToTime(dragStartPercent.value);
    startTimelineLongPress(event);
}

function onTimelineTouchMove(event: TouchEvent) {
    // Placed before the drag guard on purpose: a finger that moves must cancel the pending press even when the
    // guard below is inactive.
    moveTimelineLongPress(event);
    if (!isDragging.value || !props.timelineBounds) return;
    // Only a resolved drag writes. While 'pending' the press may still fire, and while 'reading' the tooltip is
    // open — in both cases sub-threshold jitter must not overwrite the range the user already picked.
    if (touchPhase !== 'dragging') return;
    // Superseded: the range emitted below already spans the drag origin.
    pendingTouchSelect = null;
    const currentPercent = getTimelinePercent(event);
    const startTime = percentToTime(Math.min(dragStartPercent.value, currentPercent));
    const endTime = percentToTime(Math.max(dragStartPercent.value, currentPercent));
    emit('update:startTime', startTime);
    emit('update:endTime', endTime);
}

function onTimelineTouchEnd() {
    isDragging.value = false;
    // A press that opened a tooltip consumed the gesture. Every other ending — short tap, hold over empty
    // timeline, completed drag — is a select the user still expects to land.
    if (touchPhase !== 'reading') {
        commitPendingTouchSelect();
    }
    pendingTouchSelect = null;
    touchPhase = 'idle';
    clearLongPressTimer();
}
</script>
